import { maybePropN, prop } from '@ipfabric/utils/fp/prop';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { autoSizeTableColumns } from 'components/Table/model/commands/autoSizeTableColumns';
import { selectAutoSizedColumns } from 'components/Table/model/selectors/selectAutoSizedColumns';
import { selectVisibleColumns } from 'components/Table/model/selectors/selectVisibleColumns';
import { useEffect } from 'react';
import { workaroundExtractModelCurrentState } from 'utils/hooks/useCommandModel';

export const useAutoSizedColumns = (): void => {
  const ctxProps = useTableContextSelectors({
    autoSizedColumns: selectAutoSizedColumns,
    dispatch: prop('dispatch'),
    equalWidthColumns: prop('equalWidthColumns'),
    requestedAutoSize: prop('requestedAutoSize'),
    tableContainerWidth: maybePropN('tableContainerSize', 'width'),
    someRowIsVisible: (s) =>
      s.visibleRowRange !== null &&
      s.visibleRowRange.startIndex <= s.visibleRowRange.endIndex,
    visibleColumns: selectVisibleColumns,
    data: (state) => state.data,
  });
  // There is the case when data have to be reloaded because of reset of the table settings.
  // In this case command reloadTableDataInViewport reset data to empty array and without
  // this workaround we wouldn't be able to know that. We could find out it at next render,
  // but it's too late, because hook autoSizeTableColumns would be called on stale data and
  // requestedAutoSize would be set to false after this recalculation.
  const dataEmpty =
    workaroundExtractModelCurrentState(ctxProps.dispatch).data.length === 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies(ctxProps.dispatch): stable
  // biome-ignore lint/correctness/useExhaustiveDependencies(ctxProps.tableContainerWidth): affects auto size
  useEffect(() => {
    if (
      !ctxProps.equalWidthColumns &&
      ctxProps.requestedAutoSize &&
      ctxProps.autoSizedColumns &&
      !dataEmpty &&
      ctxProps.someRowIsVisible
    ) {
      ctxProps.dispatch(autoSizeTableColumns);
    }
  }, [
    ctxProps.autoSizedColumns,
    ctxProps.equalWidthColumns,
    ctxProps.requestedAutoSize,
    ctxProps.tableContainerWidth,
    dataEmpty,
    ctxProps.someRowIsVisible,
  ]);
};
